import type {
    InferGetServerSidePropsType,
    GetServerSidePropsContext
} from 'next';
import { ApolloQueryResult } from '@apollo/client';
import { withSSROptimizely } from '@zoopla/optimizely/server';
import { initializeApollo } from '@zoopla/apollo';
import { logError } from '@zoopla/logger';
import { Taxonomy } from '@zoopla/tracking';
import { GraphQLError } from 'graphql';
import {
    HousePricesResultsQuery,
    HousePricesResultsQueryVariables
} from './queries/housePricesResults.query.generated';
import HOUSE_PRICES_RESULTS_QUERY from './queries/housePricesResults.query';
import {
    getGeoIdentifier,
    getSearchPropertiesQueryVariables,
    getAdditionalHousePricesQueryVariables
} from './helpers';

const isCrash = (e: readonly GraphQLError[]): boolean => {
    return e?.some((error: GraphQLError) => {
        const isError =
            error.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
            error.extensions?.code === 'INTERNAL_SERVER_ERROR';
        if (isError) logError('GraphQL error: ', error);
        return isError;
    });
};

const shouldThrow404 = (
    response: ApolloQueryResult<HousePricesResultsQuery>
): boolean => {
    if (response.error?.networkError) return true;

    if (response?.errors) {
        return isCrash(response.errors);
    }

    return false;
};

const _getServerSideProps = async ({
    req,
    params,
    query
}: GetServerSidePropsContext) => {
    if (typeof window === 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const newrelic = require('newrelic');
        newrelic.setTransactionName('/house-prices/');
    }

    const apolloClient = initializeApollo(null, req.headers);

    const searchPath = params?.['search-path'];

    const geoIdentifier = getGeoIdentifier(searchPath);

    if (!/^[a-z0-9/-]+$/.test(geoIdentifier)) {
        return { notFound: true as const, props: {} };
    }

    const searchPropertiesVariables = getSearchPropertiesQueryVariables(
        query,
        geoIdentifier
    );
    const additionalHousePricesVariables =
        getAdditionalHousePricesQueryVariables(query, searchPath);

    const variables = {
        ...searchPropertiesVariables,
        ...additionalHousePricesVariables
    };

    const response = await apolloClient.query<
        HousePricesResultsQuery,
        HousePricesResultsQueryVariables
    >({
        query: HOUSE_PRICES_RESULTS_QUERY,
        variables,
        errorPolicy: 'all'
    });

    if (shouldThrow404(response)) {
        return { notFound: true as const, props: {} };
    }

    let extraTaxonomy: Partial<Taxonomy> = {};

    let hasLessThan2Properties = true;
    const searchResults = response?.data?.propertiesSearch;
    if (searchResults?.__typename === 'PropertiesSearchConnection') {
        const { metaData, edges } = searchResults ?? {};

        extraTaxonomy = {
            activity: 'house_prices_search',
            section: 'house-prices',
            page: '/house-prices/results/',
            location: metaData?.input?.geoString || metaData?.geoDescription,
            countyAreaName: metaData?.geoCountyAreaName,
            regionName: metaData?.geoRegionName,
            outcodes: metaData?.geoOutcodes,
            outcode: metaData?.geoOutcode || '',
            fullStreetName: metaData?.geoDescription,
            alias: metaData?.geoAlias,
            postalArea: metaData?.geoPostalArea,
            postTownName: metaData?.geoPostTownName,
            incode: metaData?.geoIncode ?? null,
            resultsSort: variables.sortOrder?.toLowerCase() || 'address'
        };

        hasLessThan2Properties = (edges?.length ?? 0) < 2;
    }

    return {
        props: {
            data: {
                ...response?.data
            },
            noindex: hasLessThan2Properties,
            path: Array.isArray(searchPath) ? searchPath.join('/') : searchPath,
            extraTaxonomy,
            pageNumber: parseInt(query.pn as string, 10)
        }
    };
};

type GSSP = InferGetServerSidePropsType<typeof _getServerSideProps>;

export const getServerSideProps =
    withSSROptimizely.getServerSideProps<GSSP>(_getServerSideProps);
